import React, { useState, useEffect } from 'react';
import { Check, X, Clock, User, Calendar } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { getBookingRequests, approveBookingRequest, rejectBookingRequest } from '../../services/api';
import { toast } from 'sonner';
import PetAvatar from '../PetAvatar';

const BookingRequestsModal = ({ open, onOpenChange, onUpdate }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    if (open) loadRequests();
  }, [open]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const res = await getBookingRequests();
      setRequests(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setProcessing(id);
    try {
      await approveBookingRequest(id);
      toast.success('Talep onaylandı');
      loadRequests();
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Hata');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (id) => {
    setProcessing(id);
    try {
      await rejectBookingRequest(id);
      toast.success('Talep reddedildi');
      loadRequests();
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Hata');
    } finally {
      setProcessing(null);
    }
  };

  const newBookings = requests.filter(r => r.status === 'pending');
  const cancelRequests = requests.filter(r => r.status === 'cancel_requested');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Randevu Talepleri</DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="text-center text-[#8E8E93] py-6">Yükleniyor...</p>
        ) : requests.length === 0 ? (
          <div className="text-center py-8">
            <Calendar size={48} className="text-[#C7C7CC] mx-auto mb-3" />
            <p className="text-[#8E8E93]">Bekleyen talep yok</p>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            {/* New Booking Requests */}
            {newBookings.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider mb-2">
                  Yeni Randevu Talepleri ({newBookings.length})
                </h3>
                <div className="space-y-2">
                  {newBookings.map(req => (
                    <div key={req.id} className="p-3 bg-yellow-50 rounded-xl" data-testid={`request-${req.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <PetAvatar species={req.pet_species} size={18} />
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{req.customer_name}</p>
                            <p className="text-xs text-[#8E8E93]">{req.pet_name} - {req.operation_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-semibold text-gray-900">
                            {new Date(req.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                          </p>
                          <p className="text-xs text-[#6b6de3]">{req.start_time} - {req.end_time}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          onClick={() => handleApprove(req.id)}
                          disabled={processing === req.id}
                          className="flex-1 bg-[#34C759] hover:bg-[#2DA84E] rounded-lg h-8 text-xs"
                          data-testid={`approve-${req.id}`}
                        >
                          <Check size={14} className="mr-1" /> Onayla
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(req.id)}
                          disabled={processing === req.id}
                          className="flex-1 rounded-lg h-8 text-xs text-red-500 border-red-200 hover:bg-red-50"
                          data-testid={`reject-${req.id}`}
                        >
                          <X size={14} className="mr-1" /> Reddet
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cancellation Requests */}
            {cancelRequests.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider mb-2">
                  İptal Talepleri ({cancelRequests.length})
                </h3>
                <div className="space-y-2">
                  {cancelRequests.map(req => (
                    <div key={req.id} className="p-3 bg-orange-50 rounded-xl" data-testid={`cancel-request-${req.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <PetAvatar species={req.pet_species} size={18} />
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{req.customer_name}</p>
                            <p className="text-xs text-[#8E8E93]">{req.pet_name} - {req.operation_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-semibold text-gray-900">
                            {new Date(req.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                          </p>
                          <p className="text-xs text-orange-600">{req.start_time}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          onClick={() => handleApprove(req.id)}
                          disabled={processing === req.id}
                          className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-lg h-8 text-xs"
                          data-testid={`approve-cancel-${req.id}`}
                        >
                          <Check size={14} className="mr-1" /> İptali Onayla
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(req.id)}
                          disabled={processing === req.id}
                          className="flex-1 rounded-lg h-8 text-xs text-gray-600 hover:bg-gray-100"
                          data-testid={`reject-cancel-${req.id}`}
                        >
                          <X size={14} className="mr-1" /> Reddet
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingRequestsModal;
